import React from "react";
import { Button, Form } from "antd";
import type { TOrganizer } from "components/organizer/helpers/organizer-types-helper";
import Organizer from "components/organizer";
import { Localize } from "components/service";
import { useModelFormValidation } from "components/permissions";
import { emailChannelUseMutation } from "../../hooks";
import type {
  TEmailChannel,
  TEmailChannelFormValues,
} from "../../helpers/email-channel-types-helper";
import EmailChannelFormFields from "../fields";

interface IEmailChannelEditFormProps {
  organizer: TOrganizer;
  channel?: TEmailChannel;
  variables: Record<string, any>;
  action?: () => void;
}

const EmailChannelEditForm: React.FC<IEmailChannelEditFormProps> = ({
  organizer,
  channel,
  variables,
  action = () => {},
}) => {
  const [form] = Form.useForm<TEmailChannelFormValues>();
  const { validateUpdatePermission } = useModelFormValidation(Organizer.Const);
  const {
    _setEmailChannelCreateUpdate,
    loadingMutationEmailChannelCreateUpdate,
  } = emailChannelUseMutation.createUpdate(channel?.id, variables);

  const handleFinish = async (values: TEmailChannelFormValues) => {
    if (!validateUpdatePermission()) return;

    const input = {
      id: values.id,
      exhibition_type_id: values.exhibition_type_id,
      is_active: values.is_active,
      is_visible: values.is_visible,
    };

    try {
      if (channel && values.id !== channel.id) {
        await _setEmailChannelCreateUpdate({
          variables: {
            input: {
              id: channel.id,
              exhibition_type_id: null,
            },
          },
          notifySuccess: false,
          refetch: false,
        });

        try {
          await _setEmailChannelCreateUpdate({
            mutationID: null,
            variables: { input },
          });
        } catch (error) {
          await _setEmailChannelCreateUpdate({
            variables: {
              input: {
                id: channel.id,
                exhibition_type_id: channel.exhibition_type_id,
                is_active: channel.is_active === true,
                is_visible: channel.is_visible === true,
              },
            },
            notifySuccess: false,
          });

          throw error;
        }
      } else {
        await _setEmailChannelCreateUpdate({
          variables: { input },
        });
      }

      form.resetFields();
      action();
    } catch (_) {
      // Apollo onError displays the standard project notification.
    }
  };

  return (
    <Form<TEmailChannelFormValues>
      form={form}
      layout="vertical"
      className="model-form email-channel-edit-form"
      initialValues={{
        id: channel?.id,
        exhibition_type_id: channel?.exhibition_type_id,
        is_active: channel ? channel.is_active === true : true,
        is_visible: channel ? channel.is_visible === true : false,
      }}
      onFinish={handleFinish}
    >
      <EmailChannelFormFields.EmailSelect channel={channel} />
      <EmailChannelFormFields.ExhibitionTypeSelect
        organizerID={organizer.id}
        excludedExhibitionTypeIDs={[organizer.et_default_id]}
        selectedExhibitionType={channel?.relExhibitionType}
      />

      <div className="email-channel-form-states">
        <EmailChannelFormFields.StatusSwitch form={form} />
        <EmailChannelFormFields.VisibilitySwitch form={form} />
      </div>

      <div className="form-btn-holder">
        <Button className="light-bg" onClick={action}>
          <Localize>GLOBAL.Button_Text_Cancel</Localize>
        </Button>
        <Button
          className="btn-right"
          type="primary"
          htmlType="submit"
          loading={loadingMutationEmailChannelCreateUpdate}
        >
          <Localize>
            {channel
              ? "GLOBAL.Button_Text_SaveChanges"
              : "EMAIL_CHANNEL.Button_Connect"}
          </Localize>
        </Button>
      </div>
    </Form>
  );
};

export default EmailChannelEditForm;
